import * as THREE from '@phdsoft/three';
import { Util } from '@phdsoft/common';
import { Injectable } from '@angular/core';
import { Graphics } from './graphics';
import { ThreeJsCleanup } from 'src/app/shared/utils/threejs-cleanup.util';

interface Rectd {
  init: boolean;
  buttonDown: boolean;
  segments?: THREE.LineSegments;
  origx: number;
  origy: number;
  inix: number;
  endx: number;
  iniy: number;
  endy: number;
  startRectangleSelection: boolean;
}

@Injectable({ providedIn: 'root' })
export class GraphicsRenderControls {

    constructor(
      private graphics: Graphics
    ) { }

    // rectangle decal (for cloning)
    private _rectd: Rectd = {
        init: false,
        buttonDown: false,
        segments: undefined,
        origx: 0,
        origy: 0,
        inix: 0,
        endx: 0,
        iniy: 0,
        endy: 0,
        startRectangleSelection: false,
    }

    public get rectd() {
      return this._rectd;
    }
    public set rectd(value) {
      this._rectd = value;
    }

    private resetRectd(): void {
      // Dispose of objects that need cleanup
      ThreeJsCleanup.disposeObject(this._rectd.segments); // Dispose of the segments
      this._rectd.segments = undefined;

      // Reset the object to its initial configuration
      this._rectd = {
          init: false,
          buttonDown: false,
          segments: undefined,
          origx: 0,
          origy: 0,
          inix: 0,
          endx: 0,
          iniy: 0,
          endy: 0,
          startRectangleSelection: false,
      };
    }

    public insertZoomBox(ev) {
        this.threeStartGeometryCloning();
        this.graphics.rectd.startRectangleSelection = true;
    }

    public threeStartGeometryCloning() {
        if (!this.rectd.startRectangleSelection) {
            this.rectd.startRectangleSelection = true
            this.graphics.Controls.enabled = false
        }
    }

    public updateMousePosition(event) { //chamado toda vez que o mouse se movimenta
        event = event.changedTouches ? event.changedTouches[0] : event
        if (Util.isNullOrUndefined(event.clientX)) {
            event.clientX = event.center.x;
        }
        if (Util.isNullOrUndefined(event.clientY)) {
            event.clientY = event.center.y;
        }
        this.rectdDrawCloningGeometry(event.clientX - this.graphics.offsetX, event.clientY - this.graphics.offsetY, event.buttons)
    }

    /*
     *  @brief: este método cria o quadrado de caixa de seleção para
     *          delimitar o reparo que queremos fazer. Ele tem 4 etapas (ifs),
     *          a primeira delimita quando começamos e as 3 ultimas são a
     *          seleção de fato, com o startdrag, stilldrag e enddrag
     */
    public rectdDrawCloningGeometry(mouseX, mouseY, buttons) {
        if (this.rectd.startRectangleSelection) {
            if (buttons == 1) {
                this.rectd.startRectangleSelection = false
                this.rectd.init = true
            }
        }

        if (!this.rectd.buttonDown && buttons == 1) {
            // start dragging

            this.rectd.buttonDown = true
        } else if (this.rectd.buttonDown && buttons == 1 && this.rectd.init) {
            // still dragging

            if (this.rectd.segments != undefined)
                this.graphics.camera.remove(this.rectd.segments)

            if (this.rectd.origx < mouseX) {      //define a orientação no eixo x do quadrado (origem < final), ou seja,
                this.rectd.inix = this.rectd.origx  //deve ser invertida a ordem caso
                this.rectd.endx = mouseX
            } else {
                this.rectd.inix = mouseX
                this.rectd.endx = this.rectd.origx
            }

            if (this.rectd.origy < mouseY) {
                this.rectd.iniy = this.rectd.origy
                this.rectd.endy = mouseY
            } else {
                this.rectd.iniy = mouseY
                this.rectd.endy = this.rectd.origy
            }

            const distz = 20 // distance from the camera

            let p1 = this.screenToLocal(this.rectd.inix, this.rectd.iniy, distz)
            let p2 = this.screenToLocal(this.rectd.inix, this.rectd.endy, distz)
            let p3 = this.screenToLocal(this.rectd.endx, this.rectd.endy, distz)
            let p4 = this.screenToLocal(this.rectd.endx, this.rectd.iniy, distz)

            const points = [];
            points.push(p1);
            points.push(p2);

            points.push(p2);
            points.push(p3);

            points.push(p3);
            points.push(p4);

            points.push(p4);
            points.push(p1);

            const geometry = new THREE.BufferGeometry().setFromPoints(points);

            const linearObject = new THREE.LineSegments(
                geometry,
                new THREE.LineBasicMaterial({ color: 0xffffff }),
            );
            this.rectd.segments = linearObject

            this.graphics.camera.add(this.rectd.segments)
        } else if (this.rectd.buttonDown && buttons == 0 && this.rectd.init) {

            // end dragging
            this.rectd.init = false
            this.rectd.buttonDown = false
            this.graphics.Controls.enabled = true

            if (this.rectd.segments != undefined) {
                this.graphics.camera.remove(this.rectd.segments)
            }

            var control = this.graphics.Controls as any; ///orbit

            var centerX = Math.abs(this.rectd.inix - this.rectd.endx) / 2 + Math.min(this.rectd.inix, this.rectd.endx)
            var centerY = Math.abs(this.rectd.iniy - this.rectd.endy) / 2 + Math.min(this.rectd.iniy, this.rectd.endy)

            const meshToFit = this.rectd.segments
            meshToFit.geometry.computeBoundingBox();

            var dx = -(centerX - (window.innerWidth) / 2)
            var dy = -(centerY - (window.innerHeight) / 2)

            var rectWidth = Math.abs(this.rectd.inix - this.rectd.endx)
            var rectHeight = Math.abs(this.rectd.iniy - this.rectd.endy)

            var scaleByWidth = rectWidth / window.innerWidth
            var scaleByHeight = rectHeight / window.innerHeight


            var scale = Math.max(scaleByHeight, scaleByWidth)

            control.dolIn(scale, dx, dy);

            this.resetRectd();
        }

    }

    public screenToLocal(screenX, screenY, localZ) {
        let ndcX = (screenX / window.innerWidth) * 2 - 1
        let ndcY = (-screenY / window.innerHeight) * 2 + 1
        let rayc = new THREE.Raycaster()
        rayc.setFromCamera(new THREE.Vector2(ndcX, ndcY), this.graphics.camera)
        let newpos = new THREE.Vector3()
        rayc.ray.at(localZ, newpos)
        this.graphics.camera.worldToLocal(newpos)
        return newpos
    }
}
